import type { AuditChange, AuditEntityType } from '@/modules/audit/audit-types';

export const AUDIT_BATCH_WINDOW_MS = 5 * 60 * 1000;

export function uniqueDescriptors(descriptors: string[]): string[] {
  return Array.from(new Set(descriptors));
}

export function mergeChanges(prev: AuditChange[], next: AuditChange[]): AuditChange[] {
  const map = new Map<string, AuditChange>();
  for (const c of prev) map.set(c.field, c);
  for (const c of next) {
    const existing = map.get(c.field);
    if (existing) {
      const merged: AuditChange = { field: c.field, from: existing.from, to: c.to };
      if (JSON.stringify(merged.from) === JSON.stringify(merged.to)) {
        map.delete(c.field);
      } else {
        map.set(c.field, merged);
      }
    } else {
      if (JSON.stringify(c.from) !== JSON.stringify(c.to)) {
        map.set(c.field, c);
      }
    }
  }
  return Array.from(map.values());
}

export function diffFields<T extends Record<string, any>>(
  before: T | undefined | null,
  after: Partial<T>,
  keys: (keyof T)[],
): AuditChange[] {
  const out: AuditChange[] = [];
  if (!before) return out;
  for (const k of keys) {
    if (!(k in after)) continue;
    const a = before[k];
    const b = (after as any)[k];
    if (JSON.stringify(a ?? null) !== JSON.stringify(b ?? null)) {
      out.push({ field: String(k), from: a ?? null, to: b ?? null });
    }
  }
  return out;
}

export function descriptorsFor(
  entity: AuditEntityType,
  changes: AuditChange[],
): string[] {
  const fields = new Set(changes.map((c) => c.field));
  const has = (f: string) => fields.has(f);
  const descs: string[] = [];

  switch (entity) {
    case 'goal':
      if (has('title')) descs.push('renamed');
      break;
    case 'rock':
      if (has('title')) descs.push('renamed');
      if (has('ownerId')) descs.push('reassigned');
      if (has('status')) descs.push('updated');
      break;
    case 'milestone': {
      const completedChange = changes.find((c) => c.field === 'completed');
      if (completedChange && completedChange.to === true) descs.push('completed');
      if (has('endDate')) descs.push('rescheduled');
      if (has('ownerId')) descs.push('reassigned');
      if (has('title')) descs.push('renamed');
      break;
    }
    case 'metric':
      if (has('entries')) descs.push('input');
      if (has('title')) descs.push('renamed');
      if (has('ownerId')) descs.push('reassigned');
      if (has('targets')) descs.push('retargeted');
      break;
    case 'scorecard':
      if (has('ownerId')) descs.push('reassigned');
      break;
    case 'meeting':
      if (has('scheduledAt')) descs.push('rescheduled');
      if (has('hostId')) descs.push('reassigned');
      break;
    case 'meeting_schedule':
      if (has('rule') || has('start')) descs.push('rescheduled');
      if (has('hostId')) descs.push('reassigned');
      break;
    case 'process': {
      if (has('title')) descs.push('renamed');
      if (has('ownerId')) descs.push('reassigned');
      const statusChange = changes.find((c) => c.field === 'status');
      if (statusChange?.to === 'archived') descs.push('archived');
      if (has('reviewIntervalMonths')) descs.push('rescheduled');
      break;
    }
    case 'issue': {
      if (has('title')) descs.push('renamed');
      if (has('priority')) descs.push('prioritized');
      if (has('ownerId')) descs.push('reassigned');
      const statusChange = changes.find((c) => c.field === 'status');
      if (statusChange) {
        if (statusChange.to === 'resolved') descs.push('solved');
        else if (statusChange.to === 'archived') descs.push('archived');
        else if (statusChange.to === 'open') descs.push('opened');
      }
      break;
    }
    case 'task': {
      if (has('title')) descs.push('renamed');
      const completedChange = changes.find((c) => c.field === 'completed');
      if (completedChange && completedChange.to === true) descs.push('completed');
      const issueIdChange = changes.find((c) => c.field === 'issueId');
      if (issueIdChange && issueIdChange.to) descs.push('flagged');
      if (has('dueDate')) descs.push('rescheduled');
      if (has('ownerId')) descs.push('reassigned');
      break;
    }
    case 'person': {
      if (has('name')) descs.push('renamed');
      const archivedChange = changes.find((c) => c.field === 'archived');
      if (archivedChange) {
        if (archivedChange.to === true) descs.push('archived');
        else if (archivedChange.to === false) descs.push('activated');
      }
      if (has('personId')) descs.push('assigned');
      break;
    }
    case 'seat':
      if (has('title')) descs.push('renamed');
      if (has('ownerId')) descs.push('reassigned');
      if (has('tags')) descs.push('tagged');
      if (has('parentId')) descs.push('moved');
      if (has('responsibilities')) descs.push('updated');
      break;
    case 'annual_cycle':
    case 'quarterly_cycle':
      if (has('isCurrent')) descs.push('activated');
      break;
    case 'user':
    case 'invitation':
      break;
    case 'settings':
    case 'vision':
      descs.push('edited');
      break;
    case 'assessment':
      descs.push('updated');
      break;
    case 'insight': {
      const d = changes.find((c) => c.field === 'acknowledgement');
      descs.push(d?.to === 'not_true' ? 'disputed' : 'answered');
      break;
    }
  }

  return descs.length ? uniqueDescriptors(descs) : ['edited'];
}
